import { api } from "@/lib/api";

export type Protocol = "torrent" | "usenet";

export interface SearchResultRelease {
  indexer_id: number;
  indexer_name: string;
  guid: string;
  title: string;
  size: number;
  publish_date: string | null;
  download_url: string;
  info_url: string | null;
  categories: number[];
  seeders: number | null;
  leechers: number | null;
  protocol: Protocol;
  grabs: number | null;
  flags: string[];
}

export interface SearchResponse {
  total: number;
  releases: SearchResultRelease[];
}

export interface SearchArgs {
  q: string;
  /** Empty = all enabled indexers. */
  indexer_ids?: number[];
  /** Empty = no category filter. Newznab numeric category ids. */
  categories?: number[];
  limit?: number;
  /** Newznab `offset` for pagination. The backend passes it through to
   *  every queried indexer; with multiple indexers selected, page
   *  boundaries are approximate (each indexer paginates its own
   *  result list independently). */
  offset?: number;
  /** Newznab `minsize` filter in bytes. Applied server-side. */
  min_size?: number;
  /** Newznab `maxsize` filter in bytes. Applied server-side. */
  max_size?: number;
}

export interface GrabReleaseResponse {
  client_id: number;
  client_name: string;
  external_id: string;
}

export const searchApi = {
  search: async ({
    q,
    indexer_ids,
    categories,
    limit,
    offset,
    min_size,
    max_size,
  }: SearchArgs): Promise<SearchResponse> => {
    const qs = new URLSearchParams();
    qs.set("q", q);
    if (indexer_ids && indexer_ids.length > 0) {
      qs.set("indexer_ids", indexer_ids.join(","));
    }
    if (categories && categories.length > 0) {
      qs.set("categories", categories.join(","));
    }
    if (limit != null) {
      qs.set("limit", String(limit));
    }
    if (offset != null && offset > 0) {
      qs.set("offset", String(offset));
    }
    if (min_size != null && min_size > 0) {
      qs.set("min_size", String(min_size));
    }
    if (max_size != null && max_size > 0) {
      qs.set("max_size", String(max_size));
    }
    return api<SearchResponse>(`/api/v1/search?${qs.toString()}`);
  },

  /**
   * URL the browser hits to download the NZB to local disk. Returns
   * an `<a href>`-friendly path — actual GET happens in the browser
   * via `<a download>` so the user sees the Save dialog and the
   * server's `Content-Disposition` header drives the filename.
   */
  saveNzbUrl: (
    indexer_id: number,
    download_url: string,
    filename: string,
  ): string => {
    const qs = new URLSearchParams({
      indexer_id: String(indexer_id),
      download_url,
      filename,
    });
    return `/api/v1/search/nzb?${qs.toString()}`;
  },

  /**
   * Send the release to the matching download client (priority order).
   * Returns the client name + external id so the UI can confirm.
   *
   * `private` (migration 0062 + search-private): when true, the backend
   * dispatches with the `download.category.private` category. The
   * matching download-client category should be configured to write to
   * the private library root the household media server isn't mounted
   * at. Default false keeps the historical behaviour.
   */
  grab: (release: SearchResultRelease, opts: { private?: boolean } = {}) =>
    api<GrabReleaseResponse>("/api/v1/search/grab", {
      method: "POST",
      body: JSON.stringify({
        indexer_id: release.indexer_id,
        download_url: release.download_url,
        title: release.title,
        protocol: release.protocol,
        guid: release.guid,
        size: release.size,
        private: opts.private ?? false,
      }),
    }),
};
